import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import {
  acceptInterventionSwapSuggestion,
  dismissInterventionSwapSuggestion,
  scanInterventionSwapSuggestions,
} from '../lib/functions';
import { useStudents } from '../lib/useStudents';
import { Icons } from '../lib/Icons';

function studentLabel(students, id) {
  const s = students.find((st) => st.id === id);
  if (!s) return 'Unknown pupil';
  return `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown pupil';
}

export default function InterventionSwapSuggestionsPanel({ compact = false }) {
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const { students } = useStudents();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await scanInterventionSwapSuggestions({});
      setSuggestions(Array.isArray(result.data?.suggestions) ? result.data.suggestions : []);
    } catch (err) {
      console.error(err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canEdit) load();
    else setLoading(false);
  }, [canEdit, load]);

  const handleRescan = async () => {
    setScanning(true);
    try {
      const result = await scanInterventionSwapSuggestions({});
      const data = result.data || {};
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      toast.success(
        data.created
          ? `${data.created} new swap suggestion(s) found`
          : 'No new swap suggestions. Check back after more session logs'
      );
    } catch (err) {
      toast.error(err?.message || 'Could not scan for swap suggestions');
    } finally {
      setScanning(false);
    }
  };

  const handleAccept = async (suggestionId) => {
    if (!window.confirm('Swap the current pupil out and allocate the waitlisted pupil to this group?')) return;
    setBusyId(suggestionId);
    try {
      await acceptInterventionSwapSuggestion({ suggestionId });
      toast.success('Swap completed. Update session targets for the new pupil');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not complete swap');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (suggestionId) => {
    setBusyId(suggestionId);
    try {
      await dismissInterventionSwapSuggestion({ suggestionId });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
    } catch (err) {
      toast.error(err?.message || 'Could not dismiss suggestion');
    } finally {
      setBusyId(null);
    }
  };

  if (!canEdit) return null;
  if (loading) return null;
  if (suggestions.length === 0 && compact) return null;

  return (
    <Card className="border-teal-200 dark:border-teal-800">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icons.ArrowsRightLeft className="w-4 h-4 text-teal-600" />
          Waitlist swap suggestions
        </CardTitle>
        <button
          type="button"
          onClick={handleRescan}
          disabled={scanning}
          className="text-xs text-teal-700 hover:underline disabled:opacity-50 dark:text-teal-300"
        >
          {scanning ? 'Scanning...' : 'Scan groups'}
        </button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          When a pupil consistently achieves intervention targets, the system may suggest rotating in a higher-priority waitlisted pupil (common for SaLT groups at capacity).
        </p>
        {suggestions.length === 0 ? (
          <p className="text-sm text-gray-500">No pending swap suggestions.</p>
        ) : (
          suggestions.map((s) => (
            <div
              key={s.id}
              className="rounded-lg border border-teal-100 bg-teal-50/40 p-3 dark:border-teal-900 dark:bg-teal-950/20"
            >
              <p className="font-medium text-gray-900 dark:text-white text-sm">{s.interventionName || s.interventionType}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                Consider swapping{' '}
                <Link to={`/student/${s.currentStudentId}?tab=interventions`} className="text-teal-700 underline">
                  {studentLabel(students, s.currentStudentId)}
                </Link>
                {' '}({s.achievementRate}% achieved over {s.sessionsScored} sessions) for{' '}
                <Link to={`/student/${s.waitlistStudentId}?tab=interventions`} className="text-teal-700 underline">
                  {studentLabel(students, s.waitlistStudentId)}
                </Link>
                {' '}on the waitlist.
              </p>
              {s.reason && <p className="text-xs text-gray-500 mt-1">{s.reason}</p>}
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  disabled={busyId === s.id}
                  onClick={() => handleAccept(s.id)}
                  className="rounded bg-teal-600 px-3 py-1 text-xs text-white hover:bg-teal-700 disabled:opacity-50"
                >
                  Confirm swap
                </button>
                <button
                  type="button"
                  disabled={busyId === s.id}
                  onClick={() => handleDismiss(s.id)}
                  className="text-xs text-gray-600 hover:underline disabled:opacity-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
